import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import NotificationCard from './NotificationCard';
import _ from 'lodash';
import SaveButton from '../../../components/SaveButton';
import useAppInfo from '../../../hooks/appInfo.hook';
import HelpCard from '../../../components/HelpCard';
import DashboardLayout from '../../../layouts/Dashboard';
import { mobilizeiStoresService } from '../../../services/mobilizei/stores.service';
import { toast } from 'react-toastify';
import WarningCard from '../../../components/publish/WarningCard';
import { useUser } from '../../../contexts/user.context';
import { appPublishStatus, mobileAppPlatforms, platformsWithAbandonedCartNotificationsInWebviewTheme } from '../../../utils/constants';
import { showApiError } from '../../../utils/errors';

const webviewReleaseDate = "03/05/2025"; // MM/DD/YYYY

export default function AbandonedCartNotifications() {
    const { appInfo, isAndroidAppPublished, isiOSAppPublished } = useAppInfo();
    const { isWebviewTheme, user, setUser } = useUser();
    const store = _.get(user, 'store', {});

    const [notifications, setNotifications] = useState([]);
    const [originalNotifications, setOriginalNotifications] = useState([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [warningLoading, setWarningLoading] = useState(false);

    const showUnavailableWarning = () => {
        if(isWebviewTheme && !platformsWithAbandonedCartNotificationsInWebviewTheme.includes(store.platformId)
            && store.id !== "0yocOuWnMM548MCw94mM")
        {
            return true;
        }

        return false;
    }

    const androidAppNeedsUpdate = () => {
        if(isWebviewTheme && platformsWithAbandonedCartNotificationsInWebviewTheme.includes(store.platformId)
            && store.id !== "0yocOuWnMM548MCw94mM")
        {
            if(isAndroidAppPublished() && new Date(appInfo.androidLastPublishRequestedTimestamp) < new Date(webviewReleaseDate)){
                return true;
            }
        }       

        return false
    }

    const iOSAppNeedsUpdate = () => {
        if(isWebviewTheme && platformsWithAbandonedCartNotificationsInWebviewTheme.includes(store.platformId)
            && store.id !== "0yocOuWnMM548MCw94mM")
        {
            if(isiOSAppPublished() && new Date(appInfo.iOSLastPublishRequestedTimestamp) < new Date(webviewReleaseDate)){
                return true;
            }
        }       

        return false
    }

    const addNewNotification = () => {
        let notificationsCopy = JSON.parse(JSON.stringify(notifications));
        notificationsCopy.push({
            id: Math.floor(Math.random() * 999999),
            delayInMinutes: '60',
            text: '',
            title: ''
        });
        setNotifications(notificationsCopy);
    }

    const deleteNotification = (index) => {
        let notificationsCopy = JSON.parse(JSON.stringify(notifications));
        _.pullAt(notificationsCopy, [index]);
        setNotifications(notificationsCopy);
    }

    const handleChange = (key, value, index) => {
        let notificationsCopy = JSON.parse(JSON.stringify(notifications));
        notificationsCopy[index][key] = value;
        setNotifications(notificationsCopy);
    }

    const canBeSubmitted = () => {
        return !_.isEqual(originalNotifications, notifications);
    }

    const requestAppUpdate = () => {
        if(androidAppNeedsUpdate() && appInfo.androidPublishStatus !== appPublishStatus.PUBLISHING){
            setWarningLoading(true);

            mobilizeiStoresService.setMobileAppPublishStatus(
                user?.store?.platformId,
                user?.store?.id,
                mobileAppPlatforms.ANDROID,
                appPublishStatus.PUBLISHING
            )
                .then(() => {
                    let data = {}
                    _.set(data, `platforms.${mobileAppPlatforms.ANDROID}.publishStatus`, appPublishStatus.PUBLISHING);
                    _.set(data, `platforms.${mobileAppPlatforms.ANDROID}.publishStatusUpdatedAt`, new Date());
                    _.set(data, `platforms.${mobileAppPlatforms.ANDROID}.lastPublishRequestedAt`, new Date());
    
                    // Update the local user data to match the data that was 
                    // updated on the backend
                    setUser({ ...user, store: _.merge(user.store, data) });
                    toast.success('Atualização do aplicativo Android solicitada com sucesso!');
                })
                .catch(e => {
                    console.log('errro on publish', e)
    
                    showApiError(e.error);
                }).finally(() => setWarningLoading(false))
        }

        if(iOSAppNeedsUpdate() && appInfo.iOSPublishStatus !== appPublishStatus.PUBLISHING){
            setWarningLoading(true);

            mobilizeiStoresService.setMobileAppPublishStatus(
                user?.store?.platformId,
                user?.store?.id,
                mobileAppPlatforms.IOS,
                appPublishStatus.PUBLISHING
            )
                .then(() => {
                    let data = {}
                    _.set(data, `platforms.${mobileAppPlatforms.IOS}.publishStatus`, appPublishStatus.PUBLISHING);
                    _.set(data, `platforms.${mobileAppPlatforms.IOS}.publishStatusUpdatedAt`, new Date());
                    _.set(data, `platforms.${mobileAppPlatforms.IOS}.lastPublishRequestedAt`, new Date());
    
                    // Update the local user data to match the data that was 
                    // updated on the backend
                    setUser({ ...user, store: _.merge(user.store, data) });
                    toast.success('Atualização do aplicativo iOS solicitada com sucesso!');
                })
                .catch(e => {
                    console.log('errro on publish', e)
    
                    showApiError(e.error);
                }).finally(() => setWarningLoading(false))
        } 
                  
    }

    const save = () => {
        setSaving(true);

        mobilizeiStoresService.setStoreFeatureById(
            appInfo.platformId,
            appInfo.storeId,
            'abandonedCartNotifications',
            { isActive: true, notifications }
        ).then(() => {
            toast.success('Alterações salvas com sucesso!');

            setOriginalNotifications(notifications);
        }).finally(() => {
            setSaving(false);
        })
    }

    useEffect(() => {
        mobilizeiStoresService.getStoreFeatureById(
            appInfo.platformId,
            appInfo.storeId,
            'abandonedCartNotifications'
        ).then(feature => {
            setNotifications(_.get(feature, 'notifications', []));
            setOriginalNotifications(_.get(feature, 'notifications', []));
        }).finally(() => {
            setLoading(false);
        })
    }, []);

    return (
        <DashboardLayout
            Icon={ShoppingCartOutlinedIcon}
            title="Carrinho abandonado"
            subtitle="Configure notificações para lembrar seu cliente a respeito dos itens que ele deixou no carrinho"
        >
            {loading ? (
                <Box display='flex' alignItems="center" justifyContent="center" height='100%'>
                    <CircularProgress />
                </Box>
            ) : (
                <Box flexDirection="column" flexGrow={1} display='flex' justifyContent='space-between'>
                    {showUnavailableWarning() && (
                        <WarningCard
                            marginY={2}
                            content={<>
                                O envio de notificações de carrinho abandonado não é compatível com o tema Web. Selecione outro tema para poder usar esta função.
                            </>}
                        />
                    )}

                    {(androidAppNeedsUpdate() || iOSAppNeedsUpdate()) && (
                        <WarningCard
                            marginY={2}
                            content={<>
                                É necessário atualizar o seu aplicativo para utilizar essa funcionalidade. Para solicitar a atualização 
                                do seu app {warningLoading 
                                    ? <CircularProgress size={14} /> : <a href='#' onClick={requestAppUpdate}>clique aqui</a>}.
                            </>}
                        />
                    )}

                    <Box sx={{
                        paddingX: { sm: 8, xs: 0 },
                        paddingY: { sm: 4, xs: 2 }
                    }}>
                        {notifications.length > 0 ? (
                            notifications.map((notification, index) =>
                                <NotificationCard
                                    deleteNotification={() => deleteNotification(index)}
                                    handleChange={handleChange}
                                    notification={notification}
                                    index={index}
                                    key={`not-${notification.id}`}
                                />
                            )
                        ) : (
                            <div style={{ textAlign: 'center' }}>
                                <Typography variant="subtitle1" component="h2" marginBottom={4}>
                                    Clique no botão abaixo para incluir sua primeira notificação de carrinho abandonado!
                                </Typography>
                                <Typography variant="subtitle1" component="h2" marginBottom={4}>
                                    As notificações de carrinho abandonado são enviadas para aqueles clientes que saíram do seu aplicativo sem concluir um pedido, deixando itens no carrinho de compras.
                                </Typography>
                            </div>
                        )}

                        {notifications.length < 3 &&
                            <div style={{ textAlign: 'center' }}>
                                <Button variant="contained" onClick={() => addNewNotification()} >
                                    <div className="px-16">Adicionar notificação</div>
                                </Button>
                            </div>
                        }

                        <SaveButton
                            show={canBeSubmitted()}
                            onCancel={() => setNotifications(originalNotifications)}
                            onClick={() => save()}
                            isLoading={saving}
                        />
                    </Box>

                    <Box sx={{ paddingX: 4, paddingBottom: 4 }}>
                        <HelpCard
                            link="https://mobilizei.com.br/tutoriais/como-configurar-notificacoes-de-carrinho-abandonado"
                            linkText="Como configurar as notificações de carrinho abandonado"
                        />
                    </Box>
                </Box>
            )}
        </DashboardLayout >
    )
}